import { FC, useState } from 'react';

import { AlertDialogWrapper } from '../../../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import { Tooltip } from '../../../../../components/tooltip/index';
import { EventHooks, useFormat, useModalState } from '../../../../../hooks/index';
import {
  EventTypeBackend,
  NewPlanEvent,
  TransactionSignatureFields,
} from '../../../../../types/events.types';
import { sharePlanTypeTitle } from '../../../../../types/pool-plans.types';
import {
  downloadCompanyStorageFilesSequentially,
  openCompanyStorageUrlInNewTab,
} from '../../../../../utils/company-storage-url.util';
import { EventSignatureStatusCell } from '../../../components/event-signature-status-cell';
import { EventTransactionFilesCell } from '../../../components/event-transaction-files-cell';
import { FilesModal } from '../../../modals/index';
import { BasicTable } from './basic-table';
import {
  getTransactionsTableStateProps,
  itemsToFetchMap,
  TableBodyProps,
} from './event-table-types.types';
import { PaginationItem } from './pagination-item';
import { SignatureStatusTd, Td } from './table-td';

export const NewPlan: FC<TableBodyProps> = (props) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsToFetch, setItemsToFetch] = useState(itemsToFetchMap[0]);
  const [activeFilesModal, setActiveFilesModal] = useState<{
    createdAt?: Date;
    docusignFileLinks: string[];
    filesLinks: string[];
    stakeholderName: string;
  } | null>(null);
  const { format } = useFormat();

  const { items, totalPages, isLoading } = EventHooks.useTransactions<
    NewPlanEvent & TransactionSignatureFields
  >({
    eventId: props.eventId,
    eventType: EventTypeBackend.NEW_PLAN,
    companyId: props.companyId,
    currentPage,
    take: Number(itemsToFetch),
    dateFilter: props.selectedDateFilter,
    customDateFilter: props.selectedCustomDateFilter,
    transactionView: props.transactionView,
  });

  const tableStateProps = getTransactionsTableStateProps({
    selectedDateFilter: props.selectedDateFilter,
    selectedCustomDateFilter: props.selectedCustomDateFilter,
    transactionView: props.transactionView,
    isLoading,
    itemsCount: items.length,
    event: props,
    itemsToFetch: Number(itemsToFetch),
  });

  const {
    isOpen: isOpenFilesModal,
    handleCloseModal: handleCloseFilesModal,
    handleOpenModal: handleOpenFilesModal,
    toggler: filesModalToggler,
  } = useModalState();

  const handleDocumentClick = (link: string) => {
    void openCompanyStorageUrlInNewTab(props.companyId, link);
  };

  const handleDownloadAllDocuments = async (links: string[]) => {
    await downloadCompanyStorageFilesSequentially(props.companyId, links);
  };

  const handleOpenFilesModalForRow = (
    filesLinks: string[],
    stakeholderName: string,
    date: Date,
    docusignFileLinks: string[],
  ) => {
    setActiveFilesModal({
      filesLinks,
      stakeholderName,
      createdAt: date,
      docusignFileLinks,
    });
    handleOpenFilesModal();
  };

  const handleCloseFilesModalWithReset = () => {
    setActiveFilesModal(null);
    handleCloseFilesModal();
  };

  if (props.type !== EventTypeBackend.NEW_PLAN) return <></>;

  const Transactions = (
    <>
      {items.map((event) => {
        if (event.type !== EventTypeBackend.NEW_PLAN) return null;

        const {
          sharePlan,
          pool,
          filesLinks,
          date,
          signatureSignedCount,
          signatureStatus,
          signatureTotalCount,
        } = event;

        return (
          <tr key={event.id}>
            <Td value={format(sharePlan.date, 'dd/MM/yyyy')} />
            <Td value={<Tooltip content={sharePlan.name}>{sharePlan.name}</Tooltip>} />
            <Td value={<Tooltip content={pool.name}>{pool.name}</Tooltip>} />
            <Td value={<AppFormattedMessage id={sharePlanTypeTitle[sharePlan?.type]} />} />
            {props.transactionView && (
              <SignatureStatusTd
                value={
                  <EventSignatureStatusCell
                    signatureSignedCount={signatureSignedCount}
                    signatureStatus={signatureStatus}
                    signatureTotalCount={signatureTotalCount}
                  />
                }
              />
            )}
            {!props.transactionView && (
              <Td
                value={
                  <EventTransactionFilesCell
                    date={date}
                    docusignFileLinks={event.docusignFileLinks}
                    filesLinks={filesLinks}
                    linkedSignatureFileLink={event.linkedSignatureFileLink}
                    onDocumentClick={handleDocumentClick}
                    onOpenFilesModal={handleOpenFilesModalForRow}
                    parentEvent={{
                      ...props,
                      pool: event.pool ?? props.pool,
                      sharePlan: event.sharePlan ?? props.sharePlan,
                    }}
                    stakeholderName={sharePlan.name}
                  />
                }
              />
            )}
          </tr>
        );
      })}
    </>
  );

  return (
    <>
      <AlertDialogWrapper control={{ onOpenChange: filesModalToggler, open: isOpenFilesModal }}>
        <FilesModal
          createdAt={activeFilesModal?.createdAt}
          docusignFileLinks={activeFilesModal?.docusignFileLinks}
          filesLinks={activeFilesModal?.filesLinks || []}
          name={props.name}
          onClose={handleCloseFilesModalWithReset}
          onDownloadFile={handleDocumentClick}
          onSuccess={() => handleDownloadAllDocuments(activeFilesModal?.filesLinks || [])}
          stakeholderName={activeFilesModal?.stakeholderName || ''}
        />
      </AlertDialogWrapper>
      <BasicTable
        eventType={EventTypeBackend.NEW_PLAN}
        transactionView={props.transactionView}
        {...tableStateProps}
      >
        {Transactions}
      </BasicTable>
      <PaginationItem
        currentPage={currentPage}
        handleChangeCurrentPage={setCurrentPage}
        handleFetchAmountChange={setItemsToFetch}
        itemsCount={items.length}
        itemsToFetch={itemsToFetch}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
      />
    </>
  );
};
